import { Box, Grid, Typography } from "@mui/material";
import { ConditionOperator } from "components/Campaign/CampaignConditions/NewFormatCampaignConditions/ConditionOperator/ConditionOperator";
import { ConditionValue } from "components/Campaign/CampaignConditions/NewFormatCampaignConditions/ConditionValue/ConditionValue";
import { FormButtons } from "components/FormControls/FormButtons";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { ConditionReadOnlyWrapper } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/ConditionReadOnlyWrapper";
import { useMessage } from "hooks/intl";
import { TransactionItem, TransactionItemFilterType } from "types/campaigns";
import { useStyles } from "../../CampaignTransactionItemFilters.styles";
import { itemFilters } from "../../helpers";

interface Props {
  isFilterContainerOpen: boolean;
  transactionItemFilterIndex: number;
  filterIndex: number;
  replace: (filterIndex: number, value: TransactionItemFilterType) => void;
  filter: TransactionItemFilterType;
  setFieldValue: (
    field: string,
    value: TransactionItemFilterType | TransactionItem[],
  ) => void;
  remove: (filterIndex: number) => void;
  insert: (filterIndex: number, value: TransactionItemFilterType) => void;
  transactionItemsFilters: TransactionItem[];
  error?: any;
}

export const TransactionItemFilterCondition = ({
  isFilterContainerOpen,
  transactionItemFilterIndex,
  filterIndex,
  replace,
  filter,
  setFieldValue,
  remove,
  insert,
  transactionItemsFilters,
  error,
}: Props) => {
  const classes = useStyles({ isFilterContainerOpen });
  const { message } = useMessage();
  const { filters } = transactionItemsFilters[transactionItemFilterIndex];
  const isAndOperatorVisible = filterIndex + 1 < filters.length;

  return (
    <>
      {!filter.isSaved ? (
        <>
          <Box className={classes.filterEditMode}>
            <Grid container spacing={12}>
              <Grid item xs={12}>
                <Typography className={classes.conditionHeaderText}>
                  {message({ id: "editCondition" })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "conditionType" })}
                    />
                  }
                  name={`transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}].value.field`}
                  items={itemFilters.map((item) => {
                    return {
                      id: item.id,
                      label: message({ id: item.label }),
                    };
                  })}
                  data-test-id="filter-condition-type"
                  onChange={(e) => {
                    const newInputValue = e.target;
                    const selected = itemFilters.find(
                      (item) => item.id === newInputValue?.value,
                    );

                    if (selected) {
                      const newValue = {
                        value: {
                          field: selected.id,
                        },
                        type: selected.type,
                        label: selected.label,
                        isEditMode: false,
                        isSaved: false,
                      };

                      replace(
                        filterIndex,
                        newValue as TransactionItemFilterType,
                      );
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ConditionOperator
                  type={filter?.type}
                  fieldName={`transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}].condition`}
                />
              </Grid>
              <Grid item xs={12}>
                <ConditionValue
                  type={filter?.type}
                  operator={filter?.condition?.operator}
                  fieldName={`transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}].condition.value`}
                />
              </Grid>
              <Grid item xs={12}>
                <FormButtons
                  onSubmitClick={() => {
                    setFieldValue(
                      `transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}]`,
                      { ...filter, isSaved: true, isEditMode: false },
                    );
                  }}
                  onCancelClick={() => {
                    if (filter.isEditMode) {
                      setFieldValue(
                        `transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}]`,
                        { ...filter, isSaved: true, isEditMode: false },
                      );

                      return;
                    }

                    if (filters.length === 1) {
                      const updatedtransactionItemsFilters =
                        transactionItemsFilters.filter(
                          (_, index) => index !== transactionItemFilterIndex,
                        );
                      setFieldValue(
                        "transactionItemsFilters",
                        updatedtransactionItemsFilters,
                      );
                    } else {
                      remove(filterIndex);
                    }
                  }}
                  submitMessage="saveCondition"
                  cancelMessage={filter.isEditMode ? "close" : "cancel"}
                  className={classes.formButtons}
                />
              </Grid>
            </Grid>
          </Box>
          {isAndOperatorVisible && (
            <Box className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Box>
          )}
        </>
      ) : (
        <>
          {filterIndex === 0 ? (
            <Typography className={classes.savedConditionsHeader}>
              {message({ id: "transactionFilters" })}
            </Typography>
          ) : null}
          <ConditionReadOnlyWrapper
            data-test-id="filter-condition-saved"
            condition={filter}
            isRefferalCampaign={false}
            remove={() => {
              if (filters.length === 1) {
                const updatedTransactionItemsFilters =
                  transactionItemsFilters.filter(
                    (_, index) => index !== transactionItemFilterIndex,
                  );
                setFieldValue(
                  "transactionItemsFilters",
                  updatedTransactionItemsFilters,
                );
              } else {
                remove(filterIndex);
              }
            }}
            edit={() => {
              setFieldValue(
                `transactionItemsFilters[${transactionItemFilterIndex}].filters[${filterIndex}]`,
                {
                  ...filter,
                  isSaved: false,
                  isEditMode: true,
                },
              );
            }}
            duplicate={() =>
              insert(filterIndex + 1, {
                ...filter,
                isSaved: false,
                isEditMode: false,
              })
            }
            index={filterIndex}
            error={error}
          />
          {isAndOperatorVisible && (
            <Box className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Box>
          )}
        </>
      )}
    </>
  );
};
